/**
 * PerformanceSnapshotPanel — Executive Home numbers strip.
 * Patch 36.0 · Project Obsidian.
 *
 * NUMBERS ONLY. No charts, no trends, no percentages. Every value is
 * pulled straight from an authoritative backend response — we never
 * compute derived metrics client-side beyond simple counters.
 *
 * Metrics displayed:
 *   • Posts Published        — CampaignRunSummary.counters.published
 *   • Campaigns Completed    — sum of runs whose stage='published'
 *   • Assets Approved        — CampaignRunSummary.counters.approved
 *   • Failed Publications    — CampaignRunSummary.counters.failed
 *   • Pending Reviews        — AutopilotDashboard.pending_reviews
 */
import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";

import type { CampaignRun } from "@/entities/campaign-run";
import { useCampaignRunSummary, useCampaignRuns } from "@/entities/campaign-run";
import { useCurrentWorkspace } from "@/entities/workspace";
import { getAutopilotDashboard } from "@/features/autopilot/api";
import { Card } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

interface Metric {
  key: string;
  label: string;
  value: number | string;
}

export function PerformanceSnapshotPanel(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;
  const summaryQ = useCampaignRunSummary(workspaceId);
  const runsQ = useCampaignRuns(workspaceId, 100);
  const autopilotQ = useQuery({
    queryKey: ["autopilot-dashboard", workspaceId ?? ""],
    queryFn: getAutopilotDashboard,
    refetchInterval: 60_000,
    retry: false,
    enabled: !!workspaceId,
  });

  const loading =
    !workspaceId ||
    summaryQ.isLoading ||
    runsQ.isLoading ||
    autopilotQ.isLoading;

  if (loading) {
    return (
      <Card
        className="p-5 border-white/10 obsidian-card"
        data-testid="obsidian-performance-snapshot"
        data-state="loading"
      >
        <PanelTitle />
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </Card>
    );
  }

  const counters = summaryQ.data?.counters;
  const runs = runsQ.data ?? [];
  const campaignsCompleted = runs.filter(
    (r: CampaignRun) => r.stage === "published" || r.completed_at,
  ).length;

  const metrics: Metric[] = [
    { key: "posts-published", label: "Posts Published", value: counters?.published ?? 0 },
    { key: "campaigns-completed", label: "Campaigns Completed", value: campaignsCompleted },
    { key: "assets-approved", label: "Assets Approved", value: counters?.approved ?? 0 },
    { key: "failed-publications", label: "Failed Publications", value: counters?.failed ?? 0 },
    { key: "pending-reviews", label: "Pending Reviews", value: autopilotQ.data?.pending_reviews ?? 0 },
  ];

  return (
    <Card
      className="p-5 border-white/10 obsidian-card"
      data-testid="obsidian-performance-snapshot"
      data-state="populated"
    >
      <PanelTitle />
      <ul className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {metrics.map((m) => (
          <li
            key={m.key}
            data-testid={`obsidian-metric-${m.key}`}
            className="rounded-lg border border-white/5 bg-black/10 px-3 py-2.5"
          >
            <div className="text-[10px] uppercase tracking-widest text-[var(--md-fog)]">
              {m.label}
            </div>
            <div
              className="mt-1 text-xl font-semibold text-[var(--md-ivory-0)]"
              data-testid={`obsidian-metric-${m.key}-value`}
            >
              {m.value}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PanelTitle(): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <BarChart3 className="h-4 w-4 text-[var(--md-fog)]" aria-hidden />
      <h3 className="text-sm font-semibold text-[var(--md-ivory-0)]">
        Performance Snapshot
      </h3>
    </div>
  );
}
